/* NewsTicker — right-rail news feed for every directory detail page
 * (Clients, Services, Industries). Fetches /api/news for the entity
 * and renders the items written by the corresponding master agent
 * on its daily web_search pass.
 *
 * People intentionally don't use this — their detail page shows a
 * private activity feed from audit_log instead.
 */
import React from 'react';
import { Icon } from './primitives.jsx';
import { listEntityNews } from '../api.js';

const TYPE_AGENT_LABEL = {
  client:   'Client News Watcher',
  service:  'Service News Watcher',
  industry: 'Industry News Watcher',
  company:  'Organization News Watcher',
};

function relTime(value) {
  if (!value) return '';
  const ms = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(ms)) return '';
  const diff = Date.now() - ms;
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (diff < hr)     return `${Math.max(1, Math.round(diff / min))}m ago`;
  if (diff < day)    return `${Math.round(diff / hr)}h ago`;
  if (diff < 30*day) return `${Math.round(diff / day)}d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

function faviconFor(source) {
  if (!source) return null;
  const clean = source.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  if (!clean) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(clean)}&sz=32`;
}

export const NewsTicker = ({ entityType, entityId, limit = 12 }) => {
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    if (!entityType || !entityId) { setItems([]); return; }
    let cancelled = false;
    setItems(null); setError(null);
    listEntityNews(entityType, entityId, limit)
      .then(list => { if (!cancelled) setItems(list); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [entityType, entityId, limit]);

  return (
    <aside className="news-ticker">
      <div className="news-ticker__head">
        <Icon name="sparkle" size={14}/>
        <strong>News</strong>
        <span style={{color:'var(--fg-muted)',fontSize:11,marginLeft:'auto'}}>
          {TYPE_AGENT_LABEL[entityType] || ''}
        </span>
      </div>

      {error && (
        <div style={{padding:'8px 10px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12,margin:8}}>
          {error}
        </div>
      )}

      {items === null && !error && (
        <div style={{padding:14,color:'var(--fg-muted)',fontSize:12.5}}>Loading…</div>
      )}

      {items !== null && items.length === 0 && !error && (
        <div style={{padding:'12px 14px',color:'var(--fg-muted)',fontSize:12.5,lineHeight:1.5}}>
          No items yet. The {TYPE_AGENT_LABEL[entityType] || 'news watcher'} runs daily — once it picks this entity up, its findings show here.
        </div>
      )}

      {items !== null && items.length > 0 && (
        <ul className="news-ticker__list">
          {items.map(it => {
            const fav = faviconFor(it.source);
            return (
              <li key={it.id} className="news-ticker__item">
                <div style={{display:'flex',alignItems:'flex-start',gap:8}}>
                  {fav
                    ? <img src={fav} alt="" width={16} height={16} style={{flexShrink:0,marginTop:2,borderRadius:3}} onError={e => { e.currentTarget.style.display='none'; }}/>
                    : <span style={{width:16,height:16,flexShrink:0,marginTop:2}}/>}
                  <div style={{flex:1,minWidth:0}}>
                    {it.url
                      ? <a href={it.url} target="_blank" rel="noopener noreferrer" className="news-ticker__title">{it.title}</a>
                      : <div className="news-ticker__title">{it.title}</div>}
                    <div className="news-ticker__meta">
                      {it.source ? <span>{it.source}</span> : null}
                      {it.source && it.publishedAt ? <span> · </span> : null}
                      {it.publishedAt ? <span>{relTime(it.publishedAt)}</span> : null}
                    </div>
                    {it.summaryMd && (
                      <div className="news-ticker__summary">{it.summaryMd}</div>
                    )}
                    {it.whyRelevant && (
                      <div className="news-ticker__why">{it.whyRelevant}</div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
};
